import React, {Component} from 'react';
import Endpoints from 'helpers/enums/endpoints';
import buildQueryString from 'utils/build-query-string';
import BodyDataNormalizer from 'normalizers/body-data-normalizer';
import ShoeDataNormalizer from 'normalizers/shoe-data-normalizer';
import BikeDataNormalizer from 'normalizers/bike-data-normalizer';
import SaddleDataNormalizer from 'normalizers/saddle-data-normalizer';

/**
 * The ProductMatches page requires 4 different API calls so there is a large amount of logic needed
 * to determine when the page is ready/in server error/empty
 *
 * All of that API logic is captured here
 * This isn't normal practice, but is done for readability.
 */
export default class ProductMatchesData extends Component {
  /**
   * React Lifecycle Hook
   * Invoked immediately after updating occurs. This method is not called
   * for the initial render. Use this as an opportunity to operate on the
   * DOM when the component has been updated.
   *
   * @method  componentDidUpdate
   */
  componentDidUpdate(prevProps, prevState) {
    if (this.state.sessionLoaded && (this.state.sessionLoaded !== prevState.sessionLoaded)) {
      this.setInitialProduct();
    }
  }

  setInitialProduct() {
    let selectedCategory;
    let selectedType;

    if (this.state.selectedProductType) {
      selectedType = this.state.selectedProductType;
      selectedCategory = this.state.sessionData.find(el => el.type === selectedType).category
    }
    else {
      const session = this.state.sessionData.find(obj => !obj.empty) || this.state.sessionData[0] || {};
      selectedCategory = session.category;
      selectedType = session.type;
    }

    this.setState({
      'selectedCategory': selectedCategory,
      'selectedProductType': selectedType
    }, () => this.getProductData());
  }

  contentLoaded() {
    return (
      this.state.saddlesLoaded &&
      this.state.bikesLoaded &&
      this.state.shoesLoaded &&
      this.state.selectedProductType
    );
  }

  allRequestsFailed() {
    const sessionError = this.state.sessionLoaded && !this.state.sessionData;
    const saddleError = this.state.saddlesLoaded && !this.state.saddleMatches;
    const bikeError = this.state.bikesLoaded && !this.state.bikeMatches;
    const shoeError = this.state.shoesLoaded && !this.state.shoeMatches;

    return sessionError && saddleError && bikeError && shoeError;
  }

  /**
   * Utility method to build a complete url from a base url and a query params objectionable
   *
   * @method buildUrl
   * @param  {[type]} url    [Base url]
   * @param  {[type]} params [Key/value object for query parameters]
   * @return {[String]}      [A formatted url including any query params]
   */
  buildUrl(url, params) {
    const queryString = buildQueryString(params);
    return queryString ? `${url}?${queryString}` : url;
  }

  getProductData() {
    this.setState({
      'serverError': false,
    });

    this.getShoeData();
    this.getSaddleData();
    return this.getBikeData();
  }

  getSessionData() {
    this.setState({
      'serverError': false,
    });

    return this.props.apiService.get(
      Endpoints.bodyData,
      this.onServerError
    )
    .then(data => {
      const normalizedSessionData = BodyDataNormalizer.normalize(data);

      this.setState({
        'sessionLoaded': true,
        'sessionData': normalizedSessionData,
      });
    })
    .then(() => {
      if (!this.state.serverError) {
        this.toggleThemes('light');
      }
    });
  }

  getShoeData() {
    const urlParams = { 'category': this.state.selectedCategory };

    return this.props.apiService.get(
      this.buildUrl(Endpoints.shoeMatches, urlParams),
      this.onServerError
    )
    .then(data => {
      let normalizedData;

      if (data) {
        normalizedData = ShoeDataNormalizer.normalize(data);

        const recommendedSizes = { 'shoeSize': normalizedData.shoeSize, 'shoeWidth': normalizedData.shoeWidth };

        this.setState({
          'shoesLoaded': true,
          'shoeMatches': normalizedData.productMatches,
          'recommendedSizing': Object.assign({}, this.state.recommendedSizing, recommendedSizes)
        });
      } else {
        const recommendedSizes = { 'shoeSize': undefined, 'shoeWidth': undefined };

        this.setState({
          'shoesLoaded': true,
          'shoeMatches': undefined,
          'recommendedSizing': Object.assign({}, this.state.recommendedSizing, recommendedSizes)
        });
      }
    })
    .then(() => {
      if (!this.state.serverError) {
        this.toggleThemes('light');
      }
    });
  }

  getSaddleData() {
    const urlParams = { 'category': this.state.selectedCategory };

    return this.props.apiService.get(
      this.buildUrl(Endpoints.saddleMatches, urlParams),
      this.onServerError
    )
    .then(data => {
      let normalizedData;

      if (data) {
        normalizedData = SaddleDataNormalizer.normalize(data);

        const recommendedSizes = { 'saddleSize': normalizedData.saddleSize };

        this.setState({
          'saddlesLoaded': true,
          'saddleMatches': normalizedData.productMatches,
          'recommendedSizing': Object.assign({}, this.state.recommendedSizing, recommendedSizes)
        });
      } else {
        const recommendedSizes = { 'saddleSize': undefined };

        this.setState({
          'saddlesLoaded': true,
          'saddleMatches': undefined,
          'recommendedSizing': Object.assign({}, this.state.recommendedSizing, recommendedSizes)
        });
      }
    })
    .then(() => {
      if (!this.state.serverError) {
        this.toggleThemes('light');
      }
    });
  }

  getBikeData() {
    const urlParams = { 'category': this.state.selectedCategory };

    return this.props.apiService.get(
      this.buildUrl(Endpoints.bikeMatches, urlParams),
      this.onServerError
    )
    .then(data => {
      let normalizedData;

      if (data) {
        normalizedData = BikeDataNormalizer.normalize(data);

        const recommendedSizes = {
          'bikeSize': normalizedData.bikeSize,
          'bikeSizeMountain': normalizedData.bikeSizeMountain,
          'saddleHeight': normalizedData.saddleHeight
        };

        this.setState({
          'bikesLoaded': true,
          'bikeMatches': normalizedData.productMatches,
          'recommendedSizing': Object.assign({}, this.state.recommendedSizing, recommendedSizes)
        });
      } else {
        const recommendedSizes = {
          'bikeSize': undefined,
          'bikeSizeMountain': undefined,
          'saddleHeight': undefined
        };

        this.setState({
          'bikesLoaded': true,
          'bikeMatches': undefined,
          'recommendedSizing': Object.assign({}, this.state.recommendedSizing, recommendedSizes)
        });
      }
    })
    .then(() => {
      if (!this.state.serverError) {
        this.toggleThemes('light');
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/client/pages/product-matches/product-matches-data.js